var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { driverCapsTemplate, hasMediaPlayerCaps, hasMediaRecorderCaps, hasMediaStreamerCaps, } from '../../../../kaylee/DriverCaps';
import { isValid } from '../../../../kaylee/EquipmentStates';
import { Sutl } from '../../../../sutl';
import { ControlInfo } from './Control';
import { convertVideoOutputs } from './DriverDetected';
import { HttpRequest } from './HttpRequest';
import { NetworkTools } from './NetworkTools';
import { State } from './State';
import { Timeouts } from './Timeouts';
import { isAsyncHandler } from './Transport';
import { UdpCommandSocket } from './UdpCommandSocket';
import { WorkTokenQueue } from './WorkTokenQueue';
export class DriverAdapter {
    constructor(driverKey, emulatorKey, ipAddress, simulate, logFactory, translate) {
        this.driverKey = driverKey;
        this.emulatorKey = emulatorKey;
        this.ipAddress = ipAddress;
        this.simulate = simulate;
        this.logFactory = logFactory;
        this.translate = translate;
        this.caps = new State({});
        this.isReconnected = false;
        this.timeouts = new Timeouts();
        this.workQueue = new WorkTokenQueue();
        this.asyncHandlers = [];
        this.config = { equipmentId: '', equipmentType: '', ipAddress: '' };
        this.httpRequests = [];
        this._isValid = true;
        this.offlineControlInfo = {};
        this._phase = 'disconnected';
        this.pollIndices = [];
        this.pollTimeouts = [];
        this.suspendTimeout = this.timeouts.create();
        this.udpCommandSockets = []; // eslint-disable-line @typescript-eslint/no-explicit-any
        this.log = logFactory.channel('DriverAdapter');
        this.workQueue.onCancelled = (token, info) => {
            if (info !== undefined) {
                this.log.info(`cancelling phase change request: -> ${info.toPhase}`);
            }
        };
        this.workQueue.onCompleted = (token, info) => {
            if (!token.isCancelled && info !== undefined) {
                this.log.info(`completed phase change: -> ${this.phase}`);
            }
        };
        this.workQueue.onGranted = (token, info) => {
            if (info !== undefined) {
                this.log.info(`granting phase change request: ${this.phase} -> ${info.toPhase}`);
            }
        };
        this.workQueue.onRequested = (token, info) => {
            if (info !== undefined) {
                this.log.info(`requested phase change: ${this.phase} -> ${info.toPhase}`);
            }
            this.workQueue.cancelAll();
        };
    }
    // getters and setters
    get isPollingSuspended() {
        return this.suspendType !== undefined;
    }
    get isReconnecting() {
        return false;
    }
    get isValid() {
        return this._isValid;
    }
    get phase() {
        return this._phase;
    }
    set phase(value) {
        this.log.info(`${this._phase} -> ${value}`);
        this._phase = value;
    }
    // public methods
    // This method is provided as part of the Framework interface for drivers to change their state.
    changePhase(value) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info(`driver initiated phase transition: ${this._phase} -> ${value}`);
            if (!this.workQueue.isWorking) {
                this.log.info(`requesting work token for driver phase change`);
                if (value === 'configuring') {
                    yield this.configure(this.config);
                    if (this.phase !== 'configuring')
                        return;
                    yield this.control();
                }
                else if (value === 'controlling') {
                    yield this.control();
                }
                else if (value === 'disconnected') {
                    yield this.disconnect();
                }
                else {
                    this.log.error(`drivers not allowed to transition to ${value} phase`);
                }
            }
            else {
                this.log.error(`driver phase change not permitted during framework phase change`);
            }
        });
    }
    computeInitialOfflineCaps(config) {
        this.config = config;
        this.caps.update(this.getCaps(false));
    }
    configure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.requestPhaseChange('configuring');
            try {
                yield this.doConfigure(config, token);
            }
            catch (error) {
                this.log.error(`uncaught error in configure: ${error}`);
            }
            token.complete();
        });
    }
    connect(credentials, config, storage) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.requestPhaseChange('connecting');
            let result = 'connectFailed';
            try {
                result = yield this.doConnect(credentials, config, storage, token);
            }
            catch (error) {
                this.log.error(`uncaught error in connect: ${error}`);
            }
            token.complete();
            return result;
        });
    }
    control() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.requestPhaseChange('controlling');
            try {
                yield this.doControl(token);
            }
            catch (error) {
                this.log.error(`uncaught error in control: ${error}`);
            }
            token.complete();
        });
    }
    createHttpRequest(host, options, emulatorKey) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.driver === undefined)
                throw new Error(`driver undefined`);
            // Remove any previously closed requests. It would be better to do this in a registered
            // close handler for the HttpRequest.
            Sutl.filterInPlace(this.httpRequests, http => !http.isClosed);
            emulatorKey = this.simulate ? (_a = emulatorKey !== null && emulatorKey !== void 0 ? emulatorKey : this.emulatorKey) !== null && _a !== void 0 ? _a : this.driverKey : undefined;
            const http = new HttpRequest(host, this.logFactory, options !== null && options !== void 0 ? options : {}, emulatorKey);
            this.httpRequests.push(http);
            return http;
        });
    }
    createUdpCommandSocket(options, emulatorKey) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.driver === undefined)
                throw new Error(`driver undefined`);
            // Remove any previously closed udp command sockets. It would be better to do this in a
            // registered close handler for the UdpCommandSocket.
            Sutl.filterInPlace(this.udpCommandSockets, socket => !socket.isClosed);
            emulatorKey = this.simulate ? (_a = emulatorKey !== null && emulatorKey !== void 0 ? emulatorKey : this.emulatorKey) !== null && _a !== void 0 ? _a : this.driverKey : undefined;
            const socket = new UdpCommandSocket(this.logFactory, options !== null && options !== void 0 ? options : {}, emulatorKey);
            this.udpCommandSockets.push(socket);
            return socket;
        });
    }
    disconnect(expected = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.requestPhaseChange('disconnected');
            try {
                yield this.doDisconnect(expected, token);
            }
            catch (error) {
                this.log.error(`uncaught error in disconnect: ${error}`);
            }
            token.complete();
        });
    }
    getNumActiveCalls(callType) {
        var _a;
        if (this.state === undefined)
            throw new Error('state undefined');
        return (_a = this.state.calls.getNumActiveCalls(callType)) !== null && _a !== void 0 ? _a : 0;
    }
    ping(options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield NetworkTools.ping(this.ipAddress, {
                count: (_a = options === null || options === void 0 ? void 0 : options.count) !== null && _a !== void 0 ? _a : 3,
                timeout: (_b = options === null || options === void 0 ? void 0 : options.timeoutMs) !== null && _b !== void 0 ? _b : 3000,
            });
            if (result.statistics.packetsLost > 0)
                throw new Error('ping failed');
        });
    }
    query(param) {
        if (this.stateBridge === undefined)
            throw new Error('stateBridge undefined');
        this.stateBridge.query(param);
    }
    recomputeCaps() {
        if (this.phase !== 'controlling') {
            this.log.warning(`recomputeCaps called in ${this.phase} phase; ignoring`);
            return;
        }
        this.caps.update(this.getCaps());
    }
    // This is a placeholder. The child class is expected to provide a valid implementation.
    reconnect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.error(`reconnect not supported for this type of driver`);
            //await this.changePhase('initializing');
            return true;
        });
    }
    resume() {
        this.log.info(`resuming`);
        this.suspendTimeout.clear();
        this.suspendType = undefined;
        if (this.phase === 'controlling') {
            this.startPolling();
        }
    }
    sendWakeOnLan(options) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const macAddress = this.config.macAddress;
            if (macAddress === undefined) {
                this.log.error('no mac address in config');
                return;
            }
            let ipAddress = '255.255.255.255';
            if ((options === null || options === void 0 ? void 0 : options.ipAddress) !== undefined) {
                ipAddress = typeof options.ipAddress === 'boolean' ? this.ipAddress : options.ipAddress;
            }
            const simulated = `${this.simulate ? ' (simulated)' : ''}`;
            this.log.info(`sending wake on lan to ${macAddress} at ${ipAddress}${simulated}`);
            if (this.simulate) {
                (_b = (_a = this.transport.emulator) === null || _a === void 0 ? void 0 : _a.wakeOnLan) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            else {
                yield NetworkTools.wakeOnLan(ipAddress, macAddress, {
                    count: (_c = options === null || options === void 0 ? void 0 : options.count) !== null && _c !== void 0 ? _c : 5,
                    port: (_d = options === null || options === void 0 ? void 0 : options.port) !== null && _d !== void 0 ? _d : 9,
                    timeout: (_e = options === null || options === void 0 ? void 0 : options.timeoutMs) !== null && _e !== void 0 ? _e : 3000,
                });
            }
        });
    }
    set(param, value) {
        if (this.stateBridge === undefined)
            throw new Error('stateBridge undefined');
        this.stateBridge.set(param, value);
    }
    suspend(delayMs, suspendType = 'all') {
        if (delayMs === undefined) {
            this.log.info(`suspending ${suspendType} indefinitely`);
            this.suspendType = suspendType;
            this.stopPolling();
            this.suspendTimeout.clear();
        }
        else if (delayMs <= 0) {
            this.log.error(`suspend: delayMs must be > 0`);
            this.resume();
        }
        else {
            this.log.info(`suspending ${suspendType} for ${delayMs} ms`);
            this.suspendType = suspendType;
            this.stopPolling();
            this.suspendTimeout.clear();
            this.suspendTimeout.set(() => {
                this.suspendType = undefined;
                if (this.phase === 'controlling') {
                    this.log.info(`resuming`);
                    this.startPolling();
                }
            }, delayMs);
        }
    }
    suspendPolling(delayMs) {
        this.suspend(delayMs, 'polling');
    }
    // private methods
    doConfigure(config, token) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.stateBridge === undefined)
                throw new Error('stateBridge undefined');
            if (this.driver === undefined)
                throw new Error('driver undefined');
            if (this.state === undefined)
                throw new Error('state undefined');
            this.stopPolling();
            this.transport.removeCommands('poll');
            this.unregisterAsyncHandlers();
            this.config = config;
            this.phase = 'configuring';
            try {
                yield ((_b = (_a = this.driver) === null || _a === void 0 ? void 0 : _a.onBeforeConfigure) === null || _b === void 0 ? void 0 : _b.call(_a, config));
            }
            catch (error) {
                this.handleTransportError(error);
                this.log.debug('aborting configure due to phase change');
                return;
            }
            if (token.isCancelled)
                return;
            yield ((_c = this.stateBridge) === null || _c === void 0 ? void 0 : _c.configure(config));
            if (token.isCancelled)
                return;
            try {
                yield ((_e = (_d = this.driver) === null || _d === void 0 ? void 0 : _d.onConfigure) === null || _e === void 0 ? void 0 : _e.call(_d, config));
            }
            catch (error) {
                this.handleTransportError(error);
                this.log.debug('aborting configure due to phase change');
                return;
            }
            if (token.isCancelled)
                return;
            yield this.stateBridge.getPreControlValues();
            this._isValid = isValid(this.state.validated.value);
        });
    }
    doConnect(credentials, config, storage, token) {
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return 'connectFailed';
            this.phase = 'connecting';
            return 'connected';
        });
    }
    doControl(token) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.stateBridge === undefined) {
                this.log.error('stateBridge undefined');
                return;
            }
            this.registerAsyncHandlers();
            if (this._isValid)
                yield this.stateBridge.getInitialValues();
            if (token.isCancelled)
                return;
            this.startPolling();
            this.phase = 'controlling';
            yield ((_b = (_a = this.driver) === null || _a === void 0 ? void 0 : _a.onControl) === null || _b === void 0 ? void 0 : _b.call(_a));
            yield this.stateBridge.onControl();
            if (token.isCancelled)
                return;
            this.caps.update(this.getCaps());
            if (this.isReconnected) {
                this.isReconnected = false;
                this.stateBridge.query('busy');
            }
        });
    }
    doDisconnect(expected, token) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            this.stopPolling();
            this.caps.unsubscribeAll();
            try {
                this.timeouts.clearAll();
            }
            catch (error) { }
            const prevPhase = this.phase;
            this.phase = 'disconnecting';
            if (prevPhase !== 'disconnected' && prevPhase !== 'connecting') {
                try {
                    yield ((_b = (_a = this.driver) === null || _a === void 0 ? void 0 : _a.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(_a, prevPhase));
                }
                catch (error) {
                    this.handleTransportError(error);
                }
            }
            for (const http of this.httpRequests) {
                if (!http.isClosed) {
                    this.log.warning(`closing http request that the driver forgot to close`);
                    http.close();
                }
            }
            for (const socket of this.udpCommandSockets) {
                if (!socket.isClosed) {
                    this.log.warning(`closing udp command socket that the driver forgot to close`);
                    socket.close();
                }
            }
        });
    }
    doInitialize(credentials, config, storage, token) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return 'connectFailed';
            this.phase = 'initializing';
            let newResult = 'connectFailed';
            try {
                (_b = (_a = this.transport.emulator) === null || _a === void 0 ? void 0 : _a.onInitialize) === null || _b === void 0 ? void 0 : _b.call(_a, storage, config);
                newResult = (_e = (yield ((_d = (_c = this.driver) === null || _c === void 0 ? void 0 : _c.onInitialize) === null || _d === void 0 ? void 0 : _d.call(_c, credentials, config)))) !== null && _e !== void 0 ? _e : 'connected';
            }
            catch (error) {
                this.handleTransportError(error);
            }
            return newResult;
        });
    }
    getCaps(isOnline = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33;
        if (this.driver === undefined)
            throw new Error('driver undefined');
        if (this.state === undefined)
            throw new Error('state undefined');
        if (this.stateBridge === undefined)
            throw new Error('stateBridge undefined');
        this.offlineControlInfo = {};
        const caps = Object.assign({}, driverCapsTemplate);
        const controls = isOnline ? (_b = (_a = this.stateBridge) === null || _a === void 0 ? void 0 : _a.getControls()) !== null && _b !== void 0 ? _b : {} : this.driver;
        const getControlInfo = isOnline
            ? this.stateBridge.getControlInfo.bind(this.stateBridge)
            : this.getControlInfo.bind(this);
        const bridges = isOnline ? (_d = (_c = this.stateBridge) === null || _c === void 0 ? void 0 : _c.getBridges()) !== null && _d !== void 0 ? _d : {} : {};
        caps.hasBusy = (_e = getControlInfo('busy')) === null || _e === void 0 ? void 0 : _e.hasControl;
        caps.hasCalendar =
            ((_f = getControlInfo('calendar')) === null || _f === void 0 ? void 0 : _f.hasControl) && ((_g = this.config.useCalendar) !== null && _g !== void 0 ? _g : false);
        caps.hasCallInList = (_h = getControlInfo('callInList')) === null || _h === void 0 ? void 0 : _h.hasControl;
        caps.hasDirectory =
            ((_j = getControlInfo('directory')) === null || _j === void 0 ? void 0 : _j.hasControl) && ((_k = this.config.useDirectory) !== null && _k !== void 0 ? _k : false);
        const displayBlankInfo = getControlInfo('displayBlank');
        caps.hasDisplayBlank = displayBlankInfo === null || displayBlankInfo === void 0 ? void 0 : displayBlankInfo.hasControl;
        caps.hasDisplayBlankUi =
            caps.hasDisplayBlank && ((_l = this.config.displayBlankUiEnabled) !== null && _l !== void 0 ? _l : false);
        caps.isDisplayBlankStateless = caps.hasDisplayBlank && (displayBlankInfo === null || displayBlankInfo === void 0 ? void 0 : displayBlankInfo.isStateless);
        const displayFreezeInfo = getControlInfo('displayFreeze');
        caps.hasDisplayFreeze = displayFreezeInfo === null || displayFreezeInfo === void 0 ? void 0 : displayFreezeInfo.hasControl;
        caps.hasDisplayFreezeUi =
            caps.hasDisplayFreeze && ((_m = this.config.displayFreezeUiEnabled) !== null && _m !== void 0 ? _m : false);
        caps.isDisplayFreezeStateless = caps.hasDisplayFreeze && (displayFreezeInfo === null || displayFreezeInfo === void 0 ? void 0 : displayFreezeInfo.isStateless);
        caps.hasFirmwareVersion = (_o = getControlInfo('firmwareVersion')) === null || _o === void 0 ? void 0 : _o.hasControl;
        caps.hasName = (_p = getControlInfo('name')) === null || _p === void 0 ? void 0 : _p.hasControl;
        const powerInfo = getControlInfo('power');
        caps.hasPowerControl = powerInfo === null || powerInfo === void 0 ? void 0 : powerInfo.hasControl;
        caps.isPowerStateless = caps.hasPowerControl && (powerInfo === null || powerInfo === void 0 ? void 0 : powerInfo.isStateless);
        caps.isPowerToggle = caps.hasPowerControl && (powerInfo === null || powerInfo === void 0 ? void 0 : powerInfo.isToggle);
        caps.hasReboot = (_q = getControlInfo('reboot')) === null || _q === void 0 ? void 0 : _q.hasControl;
        caps.hasSerialNumber = (_r = getControlInfo('serialNumber')) === null || _r === void 0 ? void 0 : _r.hasControl;
        caps.hasZoomConnector = (_t = (_s = this.config) === null || _s === void 0 ? void 0 : _s.zoomConnectorEnabled) !== null && _t !== void 0 ? _t : false;
        // Cameras. (Translate keys to camera IDs)
        caps.cameras = {};
        for (const key of Object.keys(this.state.cameras.value)) {
            const cameraId = this.state.cameraKeys.value[key];
            if (cameraId === undefined) {
                this.log.error(`key map does not contain entry for key = ${key}`);
                continue;
            }
            caps.cameras[cameraId] = Object.assign({}, this.state.cameras.value[key]);
        }
        // Normal camera presets.
        caps.cameraPresets = {};
        let index = 0;
        if (controls.cameraPreset && this.config.cameraPresets !== undefined) {
            for (const [presetId, preset] of Object.entries(this.config.cameraPresets)) {
                caps.cameraPresets[presetId] = Object.assign({ icon: preset.icon, index: preset.index, name: preset.name }, (preset.description === undefined
                    ? undefined
                    : { description: preset.description }));
                if (preset.index > index)
                    index = preset.index + 1;
            }
        }
        // Visca camera presets.
        if (controls.cameraPreset && this.config.cameras !== undefined) {
            for (const camera of Object.values(this.config.cameras)) {
                if (!('presets' in camera) || camera.presets === undefined)
                    continue;
                for (const presetId of Object.keys(camera.presets).sort()) {
                    const preset = camera.presets[presetId];
                    caps.cameraPresets[presetId] = {
                        name: preset.name,
                        icon: preset.icon,
                        index: index++,
                    };
                    if (preset.description) {
                        caps.cameraPresets[presetId].description = preset.description;
                    }
                }
            }
        }
        // Camera self-view controls.
        caps.hasSelfView = (_v = (_u = getControlInfo('cameraSelfView')) === null || _u === void 0 ? void 0 : _u.hasControl) !== null && _v !== void 0 ? _v : false;
        caps.hasSelfViewAuto =
            (_x = (caps.hasSelfView && ((_w = getControlInfo('cameraSelfViewAuto')) === null || _w === void 0 ? void 0 : _w.hasControl))) !== null && _x !== void 0 ? _x : false;
        if (((_y = getControlInfo('cameraSelfViewPosition')) === null || _y === void 0 ? void 0 : _y.hasControl) &&
            ((_z = getControlInfo('cameraSelfViewPositions')) === null || _z === void 0 ? void 0 : _z.hasControl)) {
            caps.selfViewPositions = this.state.cameraSelfViewPositions.value;
        }
        caps.hasSelfViewUi =
            (caps.hasSelfView || caps.selfViewPositions !== undefined) &&
                ((_0 = this.config.selfViewUiEnabled) !== null && _0 !== void 0 ? _0 : false);
        // Contact closures.
        caps.contactClosures = {};
        const ccBridge = bridges.contactClosure;
        for (const contactClosureId of Object.keys(this.state.contactClosures.values)) {
            const id = ccBridge === null || ccBridge === void 0 ? void 0 : ccBridge.getEquipmentKey(contactClosureId);
            if (id === undefined) {
                this.log.error(`failed looking up contact closure id ${contactClosureId}`);
                continue;
            }
            caps.contactClosures[id] = true;
        }
        caps.disabledCalls = this.state.disabledCalls.value;
        caps.maxCalls = this.state.maxCalls.value;
        caps.callSupportsInvitation = this.state.callSupportsInvitation.value;
        // Video inputs and outputs.
        const contentSourceInfo = getControlInfo('contentSource');
        const videoInput = (controls.videoInput === undefined
            ? {}
            : controls.videoInput);
        const videoInputInfo = getControlInfo('videoInput');
        const videoInputsInfo = getControlInfo('videoInputs');
        const videoMatrixRoutesInfo = getControlInfo('videoMatrixRoutes');
        if ((videoInputsInfo === null || videoInputsInfo === void 0 ? void 0 : videoInputsInfo.hasControl) &&
            ((videoInputInfo === null || videoInputInfo === void 0 ? void 0 : videoInputInfo.hasControl) || (contentSourceInfo === null || contentSourceInfo === void 0 ? void 0 : contentSourceInfo.hasControl))) {
            caps.hasVideoInputs = true;
            caps.hasVideoSwitcher = true;
            caps.videoInputs = isOnline
                ? Sutl.deepCopy(this.state.videoInputs.value)
                : Sutl.deepCopy((_1 = this.config.videoInputs) !== null && _1 !== void 0 ? _1 : {});
            caps.videoInputsAreContent =
                (contentSourceInfo === null || contentSourceInfo === void 0 ? void 0 : contentSourceInfo.hasControl) ||
                    ((videoInputInfo === null || videoInputInfo === void 0 ? void 0 : videoInputInfo.hasControl) &&
                        'isContent' in videoInput &&
                        ((_3 = (_2 = videoInput.isContent) === null || _2 === void 0 ? void 0 : _2.call(videoInput)) !== null && _3 !== void 0 ? _3 : false));
        }
        else if (this.config.videoInputs && !this.config.synchronizedCalls) {
            caps.hasVideoInputs = true;
            if (isOnline && 'videoInputs' in this.state.detected.value) {
                const detectedVideoInputs = (_4 = this.state.detected.value.videoInputs) !== null && _4 !== void 0 ? _4 : [];
                caps.videoInputs = Sutl.deepCopy(Object.fromEntries(Object.entries(this.config.videoInputs).filter(([id, value]) => detectedVideoInputs.find(input => value.code === input.code) !==
                    undefined)));
            }
            else {
                caps.videoInputs = Sutl.deepCopy(this.config.videoInputs);
            }
            if ((videoInputInfo === null || videoInputInfo === void 0 ? void 0 : videoInputInfo.hasControl) || (videoMatrixRoutesInfo === null || videoMatrixRoutesInfo === void 0 ? void 0 : videoMatrixRoutesInfo.hasControl)) {
                caps.hasVideoSwitcher = true;
                caps.videoInputsAreContent =
                    ((_5 = this.driver) === null || _5 === void 0 ? void 0 : _5.videoInput) !== undefined &&
                        'isContent' in this.driver.videoInput &&
                        ((_8 = (_7 = (_6 = this.driver.videoInput) === null || _6 === void 0 ? void 0 : _6.isContent) === null || _7 === void 0 ? void 0 : _7.call(_6)) !== null && _8 !== void 0 ? _8 : false);
            }
            else if (contentSourceInfo === null || contentSourceInfo === void 0 ? void 0 : contentSourceInfo.hasControl) {
                caps.hasVideoSwitcher = true;
                caps.videoInputsAreContent = true;
            }
            else {
                caps.hasVideoSwitcher = false;
                caps.videoInputsAreContent = false;
            }
            if ('videoOutputs' in this.state.detected.value) {
                const videoOutputs = convertVideoOutputs((_9 = this.state.detected.value.videoOutputs) !== null && _9 !== void 0 ? _9 : []);
                caps.hasVideoOutputs = true;
                caps.videoOutputs = Sutl.deepCopy(videoOutputs);
            }
            else if ((_10 = this.config) === null || _10 === void 0 ? void 0 : _10.videoOutputs) {
                caps.hasVideoOutputs = true;
                caps.videoOutputs = Sutl.deepCopy(this.config.videoOutputs);
            }
            else {
                caps.hasVideoOutputs = false;
            }
        }
        else {
            caps.hasVideoSwitcher = false;
            caps.hasVideoInputs = false;
            caps.hasVideoOutputs = false;
        }
        if ((videoInputInfo === null || videoInputInfo === void 0 ? void 0 : videoInputInfo.hasControl) && caps.hasVideoInputs) {
            caps.isVideoInputStateless = videoInputInfo.isStateless;
        }
        // Media controls
        if ((_11 = getControlInfo('mediaOperations')) === null || _11 === void 0 ? void 0 : _11.hasControl) {
            const mediaOperations = this.state.mediaOperations.value;
            for (const videoInput of Object.values((_12 = caps.videoInputs) !== null && _12 !== void 0 ? _12 : {})) {
                const code = videoInput.code;
                if (code in mediaOperations) {
                    const mops = mediaOperations[code];
                    Object.assign(videoInput, this.getMediaCaps(mops, getControlInfo, code, isOnline));
                }
            }
        }
        // Lighting controls and presets.
        const lightingLevelInfo = getControlInfo('lightingLevel');
        const lightingPowerInfo = getControlInfo('lightingPower');
        if (this.config.lightingControls &&
            ((lightingLevelInfo === null || lightingLevelInfo === void 0 ? void 0 : lightingLevelInfo.hasControl) || (lightingPowerInfo === null || lightingPowerInfo === void 0 ? void 0 : lightingPowerInfo.hasControl))) {
            caps.lightingControls = {};
            for (const key of Object.keys(this.config.lightingControls)) {
                const ctrl = this.config.lightingControls[key];
                caps.lightingControls[key] = {
                    name: ctrl.name,
                    index: ctrl.index,
                    hasDimmer: ((_13 = lightingLevelInfo === null || lightingLevelInfo === void 0 ? void 0 : lightingLevelInfo.hasControl) !== null && _13 !== void 0 ? _13 : false) && ctrl.dimmerEnabled !== false,
                    hasPower: ((_14 = lightingPowerInfo === null || lightingPowerInfo === void 0 ? void 0 : lightingPowerInfo.hasControl) !== null && _14 !== void 0 ? _14 : false) && ctrl.powerEnabled !== false,
                };
                if (ctrl.description)
                    caps.lightingControls[key].description = ctrl.description;
            }
        }
        if (this.config.lightingPresets && ((_15 = getControlInfo('lightingPreset')) === null || _15 === void 0 ? void 0 : _15.hasControl)) {
            caps.lightingPresets = {};
            for (const key of Object.keys(this.config.lightingPresets)) {
                const prst = this.config.lightingPresets[key];
                caps.lightingPresets[key] = {
                    icon: prst.icon,
                    index: prst.index,
                    name: prst.name,
                };
                if (prst.description)
                    caps.lightingPresets[key].description = prst.description;
            }
        }
        // Shade controls and presets.
        const shadeOperationInfo = getControlInfo('shadeOperation');
        const shadePositionInfo = getControlInfo('shadePosition');
        const shadeTiltInfo = getControlInfo('shadeTilt');
        if (this.config.shadeControls && (shadeOperationInfo === null || shadeOperationInfo === void 0 ? void 0 : shadeOperationInfo.hasControl)) {
            const shadeOperations = (_17 = (_16 = bridges.shadeOperations) === null || _16 === void 0 ? void 0 : _16.getEquipmentOperations()) !== null && _17 !== void 0 ? _17 : {};
            caps.shadeControls = {};
            for (const key of Object.keys(this.config.shadeControls)) {
                const operations = (_18 = shadeOperations[key]) !== null && _18 !== void 0 ? _18 : [];
                const supportsPosition = operations.includes('position');
                const supportsTilt = operations.includes('tilt');
                caps.shadeControls[key] = {
                    hasPosition: supportsPosition,
                    hasPositionState: ((_19 = shadePositionInfo === null || shadePositionInfo === void 0 ? void 0 : shadePositionInfo.hasControl) !== null && _19 !== void 0 ? _19 : false) && supportsPosition,
                    hasTilt: supportsTilt,
                    hasTiltState: ((_20 = shadeTiltInfo === null || shadeTiltInfo === void 0 ? void 0 : shadeTiltInfo.hasControl) !== null && _20 !== void 0 ? _20 : false) && supportsTilt,
                };
            }
        }
        const shadePresetInfo = getControlInfo('shadePreset');
        if (this.config.shadePresets && (shadePresetInfo === null || shadePresetInfo === void 0 ? void 0 : shadePresetInfo.hasControl)) {
            caps.shadePresets = {};
            for (const key of Object.keys(this.config.shadePresets)) {
                caps.shadePresets[key] = true;
            }
        }
        // Usb switcher.
        const usbHostInfo = getControlInfo('usbHost');
        if (this.config.usbHosts && (usbHostInfo === null || usbHostInfo === void 0 ? void 0 : usbHostInfo.hasControl)) {
            caps.usbHosts = Sutl.deepCopy(this.config.usbHosts);
        }
        // Audio controls.
        if (isOnline) {
            caps.hasRoomMute = 'mute' in bridges;
            caps.hasRoomVolume = 'volume' in bridges;
            caps.isRoomVolumeIncDec =
                !('range' in ((_21 = controls.volume) !== null && _21 !== void 0 ? _21 : {})) ||
                    (this.state.volumeRange.value.min === 0 &&
                        this.state.volumeRange.value.max === 0) ||
                    ('volume' in bridges &&
                        ((_22 = this.state.volumesRanges.values[bridges.volume.dKey]) === null || _22 === void 0 ? void 0 : _22.min) === 0 &&
                        ((_23 = this.state.volumesRanges.values[bridges.volume.dKey]) === null || _23 === void 0 ? void 0 : _23.max) === 0);
            caps.hasRoomVolumeMute =
                'volumeMute' in bridges && ((_24 = bridges.volumeMute.isActive) !== null && _24 !== void 0 ? _24 : true);
        }
        else {
            if (controls.mute) {
                if ('getKey' in controls.mute) {
                    caps.hasRoomMute = 'mics' in this.config;
                }
                else {
                    caps.hasRoomMute = true;
                }
            }
            if (controls.volume) {
                if ('getKey' in controls.volume) {
                    caps.hasRoomVolume = 'amplifier' in this.config;
                    caps.hasRoomVolumeMute = caps.hasRoomVolume;
                }
                else {
                    caps.hasRoomVolume = true;
                    caps.isRoomVolumeIncDec = !('range' in controls.volume);
                    caps.hasRoomVolumeMute =
                        'volumeMute' in controls && !('min' in ((_25 = controls.volumeMute) !== null && _25 !== void 0 ? _25 : {}));
                }
            }
        }
        const muteInfo = getControlInfo('mute');
        const volumeInfo = getControlInfo('volume');
        caps.isRoomMuteStateless = muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.isStateless;
        caps.isRoomMuteToggle = muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.isToggle;
        caps.roomMutePowerOffRestrictions = [...((_26 = muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.powerOffRestrictions) !== null && _26 !== void 0 ? _26 : [])];
        caps.isRoomVolumeStateless =
            (volumeInfo === null || volumeInfo === void 0 ? void 0 : volumeInfo.isStateless) ||
                (this.state.volumeRange.value.min === 0 && this.state.volumeRange.value.max === 0) ||
                ('volume' in bridges &&
                    ((_27 = this.state.volumesRanges.values[bridges.volume.dKey]) === null || _27 === void 0 ? void 0 : _27.min) ===
                        0 &&
                    ((_28 = this.state.volumesRanges.values[bridges.volume.dKey]) === null || _28 === void 0 ? void 0 : _28.max) ===
                        0);
        // TODO: Either combine the Volume / RoomVolume caps or figure out how they are different.
        // For now, they are equivalent.
        caps.hasVolume = caps.hasRoomVolume;
        caps.hasVolumeMute = caps.hasRoomVolumeMute;
        caps.isVolumeStateless = caps.isRoomVolumeStateless;
        caps.isVolumeIncDec = caps.isRoomVolumeIncDec;
        if (this.config.assistiveListening) {
            if (isOnline) {
                caps.assistiveListening = {
                    hasMute: 'assistiveListeningMute' in bridges,
                    hasVolume: 'assistiveListeningVolume' in bridges,
                };
            }
            else {
                caps.assistiveListening = {
                    hasMute: (muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.hasControl) && 'mute' in this.config.assistiveListening,
                    hasVolume: (volumeInfo === null || volumeInfo === void 0 ? void 0 : volumeInfo.hasControl) && 'volume' in this.config.assistiveListening,
                };
            }
        }
        if (this.config.audioInputSources) {
            caps.audioInputSources = {};
            for (const [id, cfg] of Object.entries(this.config.audioInputSources)) {
                if (isOnline) {
                    caps.audioInputSources[id] = Object.assign(Object.assign({}, (cfg.description && { description: cfg.description })), { hasMute: 'audioInputMute' in bridges && 'mute' in cfg, hasVolume: 'audioInputVolume' in bridges && 'volume' in cfg, icon: cfg.icon, name: cfg.name });
                }
                else {
                    caps.audioInputSources[id] = Object.assign(Object.assign({}, (cfg.description && { description: cfg.description })), { hasMute: (muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.hasControl) && 'mute' in cfg, hasVolume: (volumeInfo === null || volumeInfo === void 0 ? void 0 : volumeInfo.hasControl) && 'volume' in cfg, icon: cfg.icon, name: cfg.name });
                }
            }
        }
        if (this.config.overflowRooms) {
            caps.overflowRooms = {};
            for (const [id, cfg] of Object.entries(this.config.overflowRooms)) {
                if (isOnline) {
                    caps.overflowRooms[id] = {
                        hasMute: 'overflowRoomMute' in bridges && 'mute' in cfg,
                        hasVolume: 'overflowRoomVolume' in bridges && 'volume' in cfg,
                        isHidden: (_29 = cfg.hide) !== null && _29 !== void 0 ? _29 : false,
                        name: cfg.name,
                    };
                }
                else {
                    caps.overflowRooms[id] = {
                        hasMute: (muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.hasControl) && 'mute' in cfg,
                        hasVolume: (volumeInfo === null || volumeInfo === void 0 ? void 0 : volumeInfo.hasControl) && 'volume' in cfg,
                        isHidden: (_30 = cfg.hide) !== null && _30 !== void 0 ? _30 : false,
                        name: cfg.name,
                    };
                }
            }
        }
        if (this.config.soundReinforcement) {
            caps.soundReinforcement = {};
            for (const [id, cfg] of Object.entries(this.config.soundReinforcement)) {
                if (isOnline) {
                    caps.soundReinforcement[id] = {
                        hasMute: 'soundReinforcementMute' in bridges && 'mute' in cfg,
                        hasVolume: 'soundReinforcementVolume' in bridges && 'volume' in cfg,
                        isHidden: (_31 = cfg.hide) !== null && _31 !== void 0 ? _31 : false,
                        name: cfg.name,
                    };
                }
                else {
                    caps.soundReinforcement[id] = {
                        hasMute: (muteInfo === null || muteInfo === void 0 ? void 0 : muteInfo.hasControl) && 'mute' in cfg,
                        hasVolume: (volumeInfo === null || volumeInfo === void 0 ? void 0 : volumeInfo.hasControl) && 'volume' in cfg,
                        isHidden: (_32 = cfg.hide) !== null && _32 !== void 0 ? _32 : false,
                        name: cfg.name,
                    };
                }
            }
        }
        // Zoom connector.
        caps.hasZoomConnector = (_33 = this.config.zoomConnectorEnabled) !== null && _33 !== void 0 ? _33 : false;
        return caps;
    }
    getControlInfo(controlName) {
        var _a;
        if (this.offlineControlInfo[controlName] === undefined) {
            this.offlineControlInfo[controlName] = ControlInfo.createOffline((_a = this.driver) === null || _a === void 0 ? void 0 : _a[controlName], this.config, controlName === 'telephony' ? false : undefined);
        }
        return this.offlineControlInfo[controlName];
    }
    getMediaCaps(mediaOperations, getControlInfo, code, isOnline) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        if (this.state === undefined)
            throw new Error('state undefined');
        // All the is*Stateless values are true if stateful, false if stateless, and undefined if
        // there are no caps for that feature.
        const caps = {};
        caps.mediaOperations = mediaOperations;
        if (hasMediaPlayerCaps(caps.mediaOperations)) {
            caps.isMediaPlayerStateless = !(((_b = (_a = getControlInfo('mediaPlayerState')) === null || _a === void 0 ? void 0 : _a.hasControl) !== null && _b !== void 0 ? _b : false) &&
                code in this.state.mediaPlayerState.values);
        }
        if (hasMediaRecorderCaps(caps.mediaOperations)) {
            caps.isMediaRecorderStateless = !(((_d = (_c = getControlInfo('mediaRecorderState')) === null || _c === void 0 ? void 0 : _c.hasControl) !== null && _d !== void 0 ? _d : false) &&
                code in this.state.mediaRecorderState.values);
        }
        if (hasMediaStreamerCaps(caps.mediaOperations)) {
            caps.isMediaStreamerStateless = !(((_f = (_e = getControlInfo('mediaStreamerState')) === null || _e === void 0 ? void 0 : _e.hasControl) !== null && _f !== void 0 ? _f : false) &&
                code in this.state.mediaStreamerState.values);
        }
        if (isOnline) {
            if ((_g = getControlInfo('channelKeyboardType')) === null || _g === void 0 ? void 0 : _g.hasControl) {
                if (caps.mediaOperations.includes('channelSet')) {
                    caps.channelKeyboardType = this.state.channelKeyboardType.value;
                }
            }
            else if (caps.mediaOperations.includes('channelSet')) {
                caps.channelKeyboardType = 'numeric';
            }
        }
        if (caps.mediaOperations.includes('channelUpDown') ||
            caps.mediaOperations.includes('channelSet')) {
            caps.isChannelStateless = !((_j = (_h = getControlInfo('channel')) === null || _h === void 0 ? void 0 : _h.hasControl) !== null && _j !== void 0 ? _j : false);
        }
        if (isOnline) {
            if (mediaOperations.includes('channelTunerSet') ||
                mediaOperations.includes('channelTunerToggle')) {
                caps.isChannelTunerStateless = !((_l = (_k = getControlInfo('channelTuner')) === null || _k === void 0 ? void 0 : _k.hasControl) !== null && _l !== void 0 ? _l : false);
                if ((_m = getControlInfo('channelTuners')) === null || _m === void 0 ? void 0 : _m.hasControl) {
                    caps.channelTuners = this.state.channelTuners.value;
                }
            }
        }
        if (mediaOperations.includes('closedCaptioningSet') ||
            mediaOperations.includes('closedCaptioningToggle')) {
            caps.isClosedCaptioningStateless = !((_p = (_o = getControlInfo('closedCaptioning')) === null || _o === void 0 ? void 0 : _o.hasControl) !== null && _p !== void 0 ? _p : false);
        }
        return caps;
    }
    handleTransportError(error) {
        // The transports log their own errors, so we don't need to do it here.
        if (!('code' in error)) {
            this.log.error(`${error.message}\n${error.stack}`);
        }
    }
    initialize(credentials, config, storage) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield this.requestPhaseChange('initializing');
            let result = 'connectFailed';
            try {
                result = yield this.doInitialize(credentials, config, storage, token);
            }
            catch (error) {
                this.log.error(`uncaught error in initialize: ${error}`);
            }
            token.complete();
            return result;
        });
    }
    registerAsyncHandlers() {
        var _a, _b;
        if (this.driver === undefined)
            throw new Error('driver undefined');
        const controls = (_b = (_a = this.stateBridge) === null || _a === void 0 ? void 0 : _a.getControls()) !== null && _b !== void 0 ? _b : {};
        Object.keys(controls).forEach(key => {
            const control = controls[key];
            if (control === undefined || !('asyncHandlers' in control))
                return;
            const handlers = control.asyncHandlers;
            const name = `async.${key}`;
            if (handlers === undefined)
                return;
            if (typeof handlers === 'function') {
                handlers(this.config).forEach(h => {
                    const id = this.transport.registerAsync(h.name ? h : Object.assign(Object.assign({}, h), { name }));
                    this.asyncHandlers.push(id);
                });
            }
            else if (Array.isArray(handlers)) {
                handlers.forEach(h => {
                    const id = this.transport.registerAsync(h.name ? h : Object.assign(Object.assign({}, h), { name }));
                    this.asyncHandlers.push(id);
                });
            }
            else if (isAsyncHandler(handlers)) {
                const h = handlers;
                const id = this.transport.registerAsync(h.name ? h : Object.assign(Object.assign({}, h), { name }));
                this.asyncHandlers.push(id);
            }
            else {
                Object.values(handlers).forEach(h => {
                    const id = this.transport.registerAsync(h.name ? h : Object.assign(Object.assign({}, h), { name }));
                    this.asyncHandlers.push(id);
                });
            }
        });
    }
    requestPhaseChange(toPhase) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.workQueue.requestToken({ toPhase });
        });
    }
    startPolling() {
        var _a, _b, _c, _d;
        this.log.info(`starting polling`);
        if (this.driver === undefined)
            throw new Error('driver undefined');
        if (this.stateBridge === undefined)
            throw new Error('stateBridge undefined');
        const polls = (_b = (_a = this.driver) === null || _a === void 0 ? void 0 : _a.polls) !== null && _b !== void 0 ? _b : [];
        this.pollIndices.length = polls.length;
        this.pollIndices.fill(0);
        for (let i = 0; i < polls.length; i++) {
            const poll = polls[i];
            if ('function' in poll) {
                const pollWhenDisconnected = (_c = poll.pollWhenDisconnected) !== null && _c !== void 0 ? _c : false;
                let timeout;
                if (!this.stateBridge.isConnected && !pollWhenDisconnected)
                    continue;
                const pollCallback = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.stateBridge === undefined) {
                        throw new Error('stateBridge undefined');
                    }
                    if (!this.stateBridge.isConnected && !pollWhenDisconnected)
                        return;
                    if (this.suspendType === undefined) {
                        try {
                            yield poll.function();
                        }
                        catch (error) {
                            this.handleTransportError(error);
                        }
                    }
                    if (poll.waitUntilDone) {
                        if (timeout !== undefined) {
                            timeout.destroy();
                            const index = this.pollTimeouts.findIndex(t => t === timeout);
                            if (index >= 0)
                                this.pollTimeouts.splice(index, 1);
                        }
                        timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                        this.pollTimeouts.push(timeout);
                    }
                });
                if (poll.waitUntilDone) {
                    timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                    this.pollTimeouts.push(timeout);
                }
                else {
                    this.pollTimeouts.push(this.timeouts.setInterval(pollCallback, poll.intervalMs));
                }
            }
            else if ('functions' in poll) {
                const pollWhenDisconnected = (_d = poll.pollWhenDisconnected) !== null && _d !== void 0 ? _d : false;
                let timeout;
                if (!this.stateBridge.isConnected && !pollWhenDisconnected)
                    continue;
                const pollIndex = i;
                const pollCallback = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.stateBridge === undefined) {
                        throw new Error('stateBridge undefined');
                    }
                    if (!this.stateBridge.isConnected && !pollWhenDisconnected)
                        return;
                    if (this.suspendType === undefined) {
                        if (poll.onePerInterval) {
                            try {
                                yield poll.functions[this.pollIndices[pollIndex]]();
                            }
                            catch (error) {
                                this.handleTransportError(error);
                            }
                            if (++this.pollIndices[pollIndex] >= poll.functions.length) {
                                this.pollIndices[pollIndex] = 0;
                            }
                        }
                        else {
                            for (const fn of poll.functions) {
                                try {
                                    yield fn();
                                }
                                catch (error) {
                                    this.handleTransportError(error);
                                }
                            }
                        }
                    }
                    if (poll.waitUntilDone) {
                        if (timeout !== undefined) {
                            timeout.destroy();
                            const index = this.pollTimeouts.findIndex(t => t === timeout);
                            if (index >= 0)
                                this.pollTimeouts.splice(index, 1);
                        }
                        timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                        this.pollTimeouts.push(timeout);
                    }
                });
                if (poll.waitUntilDone) {
                    timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                    this.pollTimeouts.push(timeout);
                }
                else {
                    this.pollTimeouts.push(this.timeouts.setInterval(pollCallback, poll.intervalMs));
                }
            }
            else if ('controls' in poll) {
                if (!this.stateBridge.isConnected)
                    continue;
                const pollIndex = i;
                let timeout;
                const pollCallback = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.stateBridge === undefined) {
                        throw new Error('stateBridge undefined');
                    }
                    if (!this.stateBridge.isConnected)
                        return;
                    if (this.suspendType === undefined) {
                        if (poll.onePerInterval) {
                            const name = poll.controls[this.pollIndices[pollIndex]];
                            try {
                                yield this.stateBridge.poll(name);
                            }
                            catch (error) {
                                this.handleTransportError(error);
                            }
                            if (++this.pollIndices[pollIndex] >= poll.controls.length) {
                                this.pollIndices[pollIndex] = 0;
                            }
                        }
                        else {
                            for (const name of poll.controls) {
                                try {
                                    yield this.stateBridge.poll(name);
                                }
                                catch (error) {
                                    this.handleTransportError(error);
                                }
                            }
                        }
                    }
                    if (poll.waitUntilDone) {
                        if (timeout !== undefined) {
                            timeout.destroy();
                            const index = this.pollTimeouts.findIndex(t => t === timeout);
                            if (index >= 0)
                                this.pollTimeouts.splice(index, 1);
                        }
                        timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                        this.pollTimeouts.push(timeout);
                    }
                });
                if (poll.waitUntilDone) {
                    timeout = this.timeouts.set(pollCallback, poll.intervalMs, true);
                    this.pollTimeouts.push(timeout);
                }
                else {
                    this.pollTimeouts.push(this.timeouts.setInterval(pollCallback, poll.intervalMs));
                }
            }
        }
    }
    stopPolling() {
        this.log.info(`stopping polling`);
        for (const timeout of this.pollTimeouts) {
            timeout.clear();
        }
        this.pollTimeouts.length = 0;
    }
    unregisterAsyncHandlers() {
        this.asyncHandlers.forEach(h => this.transport.unregisterAsync(h));
        this.asyncHandlers.length = 0;
    }
}
